// src/pages/settings/forms/FormBuilderDrawer.tsx
import React, { useState } from "react";
import axios from "axios";
import { Button, Input, Switch, Space, Typography, Modal, Segmented } from "antd";
import { SaveOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { DndContext, DragStartEvent, DragOverEvent, DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { DragOverlay } from "@dnd-kit/core";

import { Toolbox } from "./builderDrawer/Toolbox";
import { BuilderCanvas } from "./builderDrawer/BuilderCanvas";
import { deleteForm, Form, FormElement, FormSection, setNewForm, updateForm } from "../../../../api/formApi";
import DetailDrawer from "../../../../components/shared/DetailDrawer";
import { useNotifications } from "../../../../context/NotificationContext";
import { isWidgetName, normaliseFieldType } from "../../../../components/forms/fieldTypes";

const { confirm } = Modal;
const { Text } = Typography;

interface FormBuilderProps {
	isOpen: boolean;
	onClose: () => void;
	form: Form;
	isSubmitting: boolean;
	setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

// Older builder versions wrote a field's only question text into `label`; recover it once
// on load so the single Question input is not blank on those templates.
const recoverAuthoredText = (field: FormElement): FormElement => {
	if (isWidgetName(field.label)) return field;
	const canonical = normaliseFieldType(field.type);
	const key = canonical === "heading" || canonical === "paragraph" ? "placeholder" : "question";
	if ((field[key] ?? "").trim() !== "") return field;
	return { ...field, [key]: field.label };
};

const recoverFormText = (form: Form): Form => {
	if (!Array.isArray(form.structure)) return form;
	if (form.use_sections) {
		const sections = form.structure as FormSection[];
		return { ...form, structure: sections.map((s) => ({ ...s, fields: (s.fields || []).map(recoverAuthoredText) })) };
	}
	return { ...form, structure: (form.structure as FormElement[]).map(recoverAuthoredText) };
};

const FormBuilderDrawer: React.FC<FormBuilderProps> = ({ isOpen, onClose, form, isSubmitting, setIsSubmitting }) => {
	const { showSuccess, showError, showWarning } = useNotifications();
	const [updatedForm, setUpdatedForm] = useState<Form>(() => recoverFormText(form));
	const [activeId, setActiveId] = useState<string | null>(null);
	const [errorMessages, setErrorMessages] = useState<string[]>([]);
	const [overCanvas, setOverCanvas] = useState(false);
	const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);

	const handleSave = async () => {
		setIsSubmitting(true);
		if (updatedForm.id === 0) {
			try {
				await setNewForm(updatedForm);
				showSuccess("Form created successfully");
				onClose();
			} catch (error: unknown) {
				handleErrors(error);
			}
		} else {
			try {
				await updateForm(updatedForm);
				showSuccess("Form updated successfully");
				onClose();
			} catch (error: unknown) {
				handleErrors(error);
			}
		}
		setIsSubmitting(false);
	};

	const handleDelete = async () => {
		setIsSubmitting(true);
		try {
			await deleteForm(updatedForm.id);
			showSuccess("Form deleted successfully");
			onClose();
		} catch (error: unknown) {
			handleErrors(error);
		}
		setIsSubmitting(false);
	};

	const confirmDeleteForm = () => {
		confirm({
			title: "Confirmation",
			content: "Are you sure you want to delete this form? This cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: () => handleDelete(),
		});
	};

	const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setUpdatedForm((prev) => ({ ...prev, title: e.target.value }));
	};

	const toggleSections = (useSections: boolean) => {
		if (useSections && !updatedForm.use_sections) {
			const section: FormSection = {
				id: Date.now(),
				title: "Section 1",
				description: "",
				fields: Array.isArray(updatedForm.structure) ? (updatedForm.structure as FormElement[]) : [],
				order: 0,
			};
			setUpdatedForm((prev) => ({
				...prev,
				use_sections: true,
				structure: [section],
			}));
			setCurrentSectionIndex(0);
		} else if (!useSections && updatedForm.use_sections) {
			const allFields = (updatedForm.structure as FormSection[]).flatMap((s) => s.fields);
			setUpdatedForm((prev) => ({
				...prev,
				use_sections: false,
				structure: allFields,
			}));
		}
	};

	const addSection = () => {
		const sections = updatedForm.structure as FormSection[];
		const newSection: FormSection = {
			id: Date.now(),
			title: `Section ${sections.length + 1}`,
			description: "",
			fields: [],
			order: sections.length,
		};
		setUpdatedForm((prev) => ({
			...prev,
			structure: [...sections, newSection],
		}));
		setCurrentSectionIndex(sections.length);
	};

	const deleteSection = (sectionId: string | number) => {
		const sections = updatedForm.structure as FormSection[];
		if (sections.length <= 1) {
			showWarning("Form must have at least one section");
			return;
		}
		const filtered = sections.filter((s) => s.id !== sectionId);
		setUpdatedForm((prev) => ({ ...prev, structure: filtered }));
		if (currentSectionIndex >= filtered.length) {
			setCurrentSectionIndex(filtered.length - 1);
		}
	};

	const updateSection = (sectionId: string | number, updates: Partial<FormSection>) => {
		const sections = updatedForm.structure as FormSection[];
		setUpdatedForm((prev) => ({
			...prev,
			structure: sections.map((s) => (s.id === sectionId ? { ...s, ...updates } : s)),
		}));
	};

	const handleUpdateField = (sectionId: string | number | undefined, fieldId: string | number, newValues: Partial<FormElement>) => {
		if (updatedForm.use_sections && sectionId !== undefined) {
			const sections = updatedForm.structure as FormSection[];
			setUpdatedForm((prev) => ({
				...prev,
				structure: sections.map((s) => (s.id === sectionId ? { ...s, fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...newValues } : f)) } : s)),
			}));
		} else {
			const fields = updatedForm.structure as FormElement[];
			setUpdatedForm((prev) => ({
				...prev,
				structure: fields.map((f) => (f.id === fieldId ? { ...f, ...newValues } : f)),
			}));
		}
	};

	const handleDeleteField = (sectionId: string | number | undefined, fieldId: string | number) => {
		if (updatedForm.use_sections && sectionId !== undefined) {
			const sections = updatedForm.structure as FormSection[];
			setUpdatedForm((prev) => ({
				...prev,
				structure: sections.map((s) => (s.id === sectionId ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) } : s)),
			}));
		} else {
			const fields = updatedForm.structure as FormElement[];
			setUpdatedForm((prev) => ({
				...prev,
				structure: fields.filter((f) => f.id !== fieldId),
			}));
		}
	};

	const handleReorderFields = (sectionId: string | number | undefined, newOrder: FormElement[]) => {
		if (updatedForm.use_sections && sectionId !== undefined) {
			const sections = updatedForm.structure as FormSection[];
			setUpdatedForm((prev) => ({
				...prev,
				structure: sections.map((s) => (s.id === sectionId ? { ...s, fields: newOrder } : s)),
			}));
		} else {
			setUpdatedForm((prev) => ({ ...prev, structure: newOrder }));
		}
	};

	const handleDragStart = (event: DragStartEvent) => {
		setActiveId(event.active.id as string);
	};

	const handleDragOver = (event: DragOverEvent) => {
		const { active, over } = event;
		if (active.data.current?.from === "toolbox") {
			if (over && (over.id === "builder-canvas" || over.data.current?.from === "canvas")) {
				setOverCanvas(true);
			} else {
				setOverCanvas(false);
			}
		}
	};

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		setOverCanvas(false);
		if (!over) return;

		if (active.data.current?.from === "toolbox" && (over.id === "builder-canvas" || over.data.current?.from === "canvas")) {
			const draggedData = active.data.current;
			const newField: FormElement = {
				id: String(new Date().getTime()),
				type: draggedData.type,
				options: draggedData.options || [],
			};

			if (updatedForm.use_sections) {
				const sections = updatedForm.structure as FormSection[];
				const currentSection = sections[currentSectionIndex];
				setUpdatedForm((prev) => ({
					...prev,
					structure: sections.map((s) => (s.id === currentSection.id ? { ...s, fields: [...s.fields, newField] } : s)),
				}));
			} else {
				const fields = updatedForm.structure as FormElement[];
				setUpdatedForm((prev) => ({
					...prev,
					structure: [...fields, newField],
				}));
			}
		} else if (active.data.current?.from === "canvas" && over.data.current?.from === "canvas") {
			if (active.id !== over.id) {
				if (updatedForm.use_sections) {
					const sections = updatedForm.structure as FormSection[];
					const currentSection = sections[currentSectionIndex];
					const oldIndex = currentSection.fields.findIndex((f) => f.id === active.id);
					const newIndex = currentSection.fields.findIndex((f) => f.id === over.id);
					if (oldIndex !== -1 && newIndex !== -1) {
						const reordered = arrayMove(currentSection.fields, oldIndex, newIndex);
						handleReorderFields(currentSection.id, reordered);
					}
				} else {
					const fields = updatedForm.structure as FormElement[];
					const oldIndex = fields.findIndex((f) => f.id === active.id);
					const newIndex = fields.findIndex((f) => f.id === over.id);
					if (oldIndex !== -1 && newIndex !== -1) {
						const reordered = arrayMove(fields, oldIndex, newIndex);
						handleReorderFields(undefined, reordered);
					}
				}
			}
		}
	};

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response) {
			if (error.response.data?.errors) {
				const messages = Object.entries(error.response.data.errors).flatMap(([_, errs]) => errs as string);
				setErrorMessages(messages);
			} else if (error.response.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message || "An unknown error occurred."]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const getCurrentSection = (): FormSection | null => {
		if (!updatedForm.use_sections) return null;
		const sections = updatedForm.structure as FormSection[];
		return sections[currentSectionIndex] || null;
	};

	return (
		<DetailDrawer
			open={isOpen}
			onClose={onClose}
			width="90vw"
			title={form.id ? "Edit Form" : "Create Form"}
			noPadding
			headerActions={
				<Space>
					{form.id !== 0 && !form.is_global && (
						<Button danger icon={<DeleteOutlined />} loading={isSubmitting} onClick={confirmDeleteForm}>
							Delete
						</Button>
					)}
					<Button type="primary" icon={<SaveOutlined />} loading={isSubmitting} onClick={handleSave}>
						Save
					</Button>
				</Space>
			}
			errorMessages={errorMessages}
			onClearErrors={() => setErrorMessages([])}
		>
			<DndContext onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
				<div style={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
					<Toolbox />

					<div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
						{/* Builder top bar */}
						<div
							style={{
								padding: "12px 16px",
								borderBottom: "1px solid var(--mg-border)",
								background: "var(--mg-bg-elevated)",
								flexShrink: 0,
								zIndex: 1,
							}}
						>
							<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
								<Input placeholder="Form Title" value={updatedForm.title} onChange={handleTitleChange} />

								<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
									<Segmented
										size="small"
										value={updatedForm.form_type || 'patient'}
										onChange={(v) => setUpdatedForm((prev) => ({ ...prev, form_type: v as 'patient' | 'practitioner' }))}
										options={[
											{ label: 'Patient Form', value: 'patient' },
											{ label: 'Practitioner Form', value: 'practitioner' },
										]}
									/>
									<Space size="small">
										<Text style={{ fontSize: 12 }}>
											{updatedForm.use_sections ? "Using Sections" : "Single Page"}
										</Text>
										<Switch
											checked={updatedForm.use_sections || false}
											onChange={(checked) => toggleSections(checked)}
											size="small"
										/>
									</Space>
									{updatedForm.use_sections && (
										<Button icon={<PlusOutlined />} onClick={addSection}>
											Add Section
										</Button>
									)}
								</div>
							</div>
						</div>

						{/* Section tab bar */}
						{updatedForm.use_sections && (
							<div
								style={{
									display: "flex",
									gap: 8,
									padding: "8px 16px",
									borderBottom: "1px solid var(--mg-border)",
									background: "var(--mg-bg-elevated)",
									flexShrink: 0,
									overflowX: "auto",
								}}
							>
								{(updatedForm.structure as FormSection[]).map((section, idx) => (
									<Button
										key={section.id}
										type={currentSectionIndex === idx ? "primary" : "default"}
										onClick={() => setCurrentSectionIndex(idx)}
									>
										{section.title}
									</Button>
								))}
							</div>
						)}

						{/* Canvas scroll area */}
						<div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
							{updatedForm.use_sections && getCurrentSection() && (
								<div style={{ marginBottom: 16 }}>
									<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
										<Input
											placeholder="Section Title"
											value={getCurrentSection()?.title || ""}
											onChange={(e) => updateSection(getCurrentSection()!.id, { title: e.target.value })}
											style={{ width: 300 }}
										/>
										<Input
											placeholder="Section Description (Optional)"
											value={getCurrentSection()?.description || ""}
											onChange={(e) => updateSection(getCurrentSection()!.id, { description: e.target.value })}
											style={{ flex: 1 }}
										/>
										{(updatedForm.structure as FormSection[]).length > 1 && (
											<Button
												danger
												icon={<DeleteOutlined />}
												onClick={() => deleteSection(getCurrentSection()!.id)}
											/>
										)}
									</div>
								</div>
							)}

							<BuilderCanvas
								formFields={updatedForm.use_sections ? getCurrentSection()?.fields || [] : (updatedForm.structure as FormElement[])}
								sectionId={updatedForm.use_sections ? getCurrentSection()?.id : undefined}
								onUpdateField={handleUpdateField}
								onDeleteField={handleDeleteField}
								onReorderFields={handleReorderFields}
								highlight={overCanvas}
							/>
						</div>
					</div>
				</div>

				<DragOverlay>
					{activeId ? (
						<div
							style={{
								padding: "8px 12px",
								border: "1px solid var(--mg-border)",
								borderRadius: 6,
								background: "var(--mg-bg-elevated)",
								fontSize: 12,
								color: "var(--mg-text)",
								boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
							}}
						>
							{activeId}
						</div>
					) : null}
				</DragOverlay>
			</DndContext>
		</DetailDrawer>
	);
};

export default FormBuilderDrawer;
